import { produce } from 'immer';
import { atom, selector } from 'recoil';

import { vehiclesState } from '@/states/used-cars/vehicles.state';
import { CarFilter } from '@/types/filters/filters.types';
import { guardRecoilDefaultValue } from '@/utils/atom';

export const bodyTypesState = atom<CarFilter[]>({
  key: 'bodyTypes',
  default: [],
});

export const bodyTypeSearchOptionsSelector = selector({
  key: 'bodyTypeSearchOptionsSelector',
  get: ({ get }) => {
    const bodyTypes = get(bodyTypesState);
    const bodyTypeSearchOptions: CarFilter[] = bodyTypes.filter(
      (option) => !option.isSelected,
    );

    return { bodyTypeSearchOptions };
  },
});

export const toggleBodyType = selector<number | null>({
  key: 'toggleBodyType',
  get: () => null,
  set: ({ set, get }, bodyTypeId) => {
    const bodyTypes = get(bodyTypesState);

    const updatedBodyType = produce(bodyTypes, (bodyTypesDraft) => {
      if (bodyTypeId) {
        const bodyType = bodyTypesDraft.find(
          (bodyType) => bodyType.id === bodyTypeId,
        );

        bodyType!.isSelected = !bodyType?.isSelected;
        return;
      }
    });

    set(bodyTypesState, updatedBodyType);
  },
});

export const selectedBodyTypesSelector = selector<CarFilter[]>({
  key: 'selectedBodyTypes',
  get: ({ get }) => {
    const bodyTypes = get(bodyTypesState);

    return bodyTypes.filter((bodyType) => bodyType.isSelected);
  },
});

export const visibleBodyTypesSelector = selector({
  key: 'visibleBodyTypesSelector',
  get: ({ get }) => {
    const bodyTypes = get(bodyTypesState);
    const vehicles = get(vehiclesState);

    const bodyTypesId = [
      ...new Set(vehicles.map((vehicle) => vehicle.bodyTypeId)),
    ];

    return bodyTypes.map((bodyType) => ({
      ...bodyType,
      isDisabled: !bodyTypesId.includes(bodyType.id),
    }));
  },
});

export const hasSelectedBodyTypesSelector = selector<boolean>({
  key: 'hasSelectedBodyTypesSelector',
  get: ({ get }) => {
    return Boolean(get(selectedBodyTypesSelector).length);
  },
});

export const resetSelectedBodyTypesSelector = selector<number | null>({
  key: 'resetSelectedBodyTypes',
  get: () => null,
  set: ({ set, get }, bodyTypeId) => {
    const bodyTypes = get(bodyTypesState);

    const updatedBodyType = produce(bodyTypes, (bodyTypesDraft) => {
      if (bodyTypeId) {
        const bodyType = bodyTypesDraft.find(
          (bodyType) => bodyType.id === bodyTypeId,
        );

        bodyType!.isSelected = false;
        return;
      }

      bodyTypesDraft.forEach((bodyType) => {
        if (bodyTypeId && bodyType.id === bodyTypeId) {
          bodyType.isSelected = false;
          return;
        }
        bodyType.isSelected = false;
      });
    });

    set(bodyTypesState, updatedBodyType);
  },
});

export const selectBodyType = selector<string[]>({
  key: 'selectBodyType',
  get: () => [],
  set: ({ set, get }, bodyTypeIds) => {
    const bodyTypes = get(bodyTypesState);

    const updatedBodyType = produce(bodyTypes, (bodyTypesDraft) => {
      if (guardRecoilDefaultValue(bodyTypeIds)) return;
      if (bodyTypeIds.length) {
        bodyTypeIds
          .map((bodyTypeId) => Number(bodyTypeId))
          .forEach((bodyTypeId) => {
            const bodyType = bodyTypesDraft.find(
              (bodyType) => bodyType.id === bodyTypeId,
            );

            if (!bodyType) return;
            bodyType.isSelected = true;
          });

        return;
      }
    });

    set(bodyTypesState, updatedBodyType);
  },
});
